import type { ReploElementVersionKind } from "schemas/element";
import type { ReploElementVersionRevision } from "schemas/generated/element";

import * as React from "react";

import { Avatar } from "@v1/components/common/designSystem/components/avatar/Avatar";
import { Tooltip } from "@v1/components/common/designSystem/components/tooltip/Tooltip";
import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import {
  restoreElementRevision,
  selectDraftElementId,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { elementRouter } from "@v2/trpc/routes/element";
import {
  differenceInCalendarWeeks,
  format,
  formatDistanceToNowStrict,
} from "date-fns";
import { Clock, CopyX, Info, Save, Send, SkipBack } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { ReploElementVersionKinds } from "schemas/element";

type CardProps = {
  onClick?(e: React.MouseEvent<HTMLDivElement>): void;
  className?: string;
  isActive: boolean;
  version: ReploElementVersionRevision;
  isDateOnly?: boolean;
};

export const formatVersionHistoryDateAndTime = (date: string) => {
  const dateObj = new Date(date);
  if (differenceInCalendarWeeks(dateObj, new Date()) < -1) {
    return format(dateObj, "MMMM d, hh:mma");
  }
  return `${formatDistanceToNowStrict(dateObj)} ago`;
};

const getVersionTitle = (version: ReploElementVersionRevision) => {
  return exhaustiveSwitch({ type: version.kind })({
    automatic: () => "Autosave",
    automaticElementOutdated: () => "Autosave",
    publish: () => "Published Page",
    unpublish: () => "Unpublished",
    revert: () => "Restored to Past Version",
    current: () => version.title,
  });
};

const VersionIcon = ({
  kind,
  isActive,
}: {
  kind: ReploElementVersionKind;
  isActive: boolean;
}) => {
  const icon = exhaustiveSwitch({ type: kind })({
    current: <Clock strokeWidth={1} size={12} />,
    automatic: <Save strokeWidth={1} size={12} />,
    automaticElementOutdated: <Save strokeWidth={1} size={12} />,
    publish: <Send strokeWidth={1} size={12} />,
    unpublish: <CopyX strokeWidth={1} size={12} />,
    revert: <SkipBack strokeWidth={1} size={12} />,
  });
  return (
    <Badge
      UNSAFE_className={twMerge(
        "bg-surface h-5 w-5",
        isActive && "text-primary bg-sky-soft",
      )}
      type="icon"
      icon={icon}
    />
  );
};

export default React.forwardRef<HTMLDivElement, CardProps>(function VersionCard(
  { className, isActive, onClick, version, isDateOnly }: CardProps,
  ref,
) {
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const elementId = useEditorSelector(selectDraftElementId);
  const { createdBy, createdAt, kind, revertedTo } = version;
  const title = getVersionTitle(version);
  const date =
    kind === ReploElementVersionKinds.current
      ? "Now"
      : formatVersionHistoryDateAndTime(createdAt);
  const userName = createdBy?.name ?? createdBy?.email;
  const defaultClassNames = twMerge(
    "flex items-start flex-row pb-6 cursor-pointer w-full",
    isDateOnly && "pl-5",
    className,
  );

  const formattedTitle = isDateOnly
    ? format(new Date(createdAt), "MMMM d, hh:mma")
    : title;

  const showRestoreButton = isActive && version.kind !== "current";
  const utils = elementRouter.useUtils();
  const {
    mutateAsync: revertElementVersion,
    isPending: isRestoreRevisionLoading,
  } = elementRouter.revertToRevision.useMutation({
    onSuccess: (data) => {
      void utils.findRevisionsMetadata.invalidate({ elementId });
      dispatch(restoreElementRevision(data.element));
    },
  });
  return (
    <div className={defaultClassNames} onClick={onClick} ref={ref}>
      {!isDateOnly && (
        <div className="z-[1]">
          <VersionIcon isActive={isActive} kind={version.kind} />
        </div>
      )}
      <div
        className={twMerge(
          "corner-[6px] ml-1.5 flex grow flex-col gap-1 rounded-sm p-1.5 pt-1",
          isActive ? "bg-selectable-selected" : "hover:bg-sheet bg-white",
        )}
      >
        <h3 className="text-xs font-semibold text-default truncate font-semibold">
          {revertedTo ? (
            <Tooltip
              triggerAsChild
              sideOffset={5}
              align="start"
              alignOffset={-5}
              content={`From ${formatVersionHistoryDateAndTime(revertedTo)}`}
            >
              <div className="flex items-center">
                <div>{formattedTitle}</div>
                <div tabIndex={0} className="ml-1">
                  <Info size={12} />
                </div>
              </div>
            </Tooltip>
          ) : (
            formattedTitle
          )}
        </h3>
        {!isDateOnly && <span className="text-muted text-xs">{date}</span>}
        {!isDateOnly && (
          <div className="flex items-center">
            <Avatar
              name={userName}
              UNSAFE_className="mr-1"
              isSquare
              size="xxs"
            />
            <span className="text-muted text-xs">{userName}</span>
          </div>
        )}
        {showRestoreButton && (
          <DeprecatedButton
            variant="primary"
            size="sm"
            layoutClassName="w-full mt-1.5"
            onClick={() => {
              if (elementId && version.id) {
                logEvent("editor.history.restore");
                void revertElementVersion({
                  elementId,
                  elementRevisionId: version.id,
                });
              }
            }}
            isLoading={isRestoreRevisionLoading}
          >
            Restore this version
          </DeprecatedButton>
        )}
      </div>
    </div>
  );
});
